import { useSyncExternalStore } from "react";
import {
  APPEARANCE_KEY,
  isAppearancePref,
  resolveScheme,
  type AppearancePref,
  type Scheme,
} from "@todotogether/shared";

/**
 * Web's half of the shared appearance model (ticket #122): the
 * persisted system/light/dark preference lives in localStorage under
 * the same key mobile stores in AsyncStorage; "system" follows the OS
 * through prefers-color-scheme and keeps following it live. The
 * resolved scheme is applied as the data-theme attribute on <html>,
 * which selects the dark value set of the stationery token table
 * (index.css). Both HTML entries carry a pre-hydration script that
 * makes the same decision before first paint — this module remains its
 * source of truth after mount (and re-applies, so a stale attribute
 * can never outlive the store).
 */

const darkQuery = (): MediaQueryList =>
  window.matchMedia("(prefers-color-scheme: dark)");

function systemScheme(): Scheme {
  return darkQuery().matches ? "dark" : "light";
}

export function readAppearancePref(): AppearancePref {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    return isAppearancePref(raw) ? raw : "system";
  } catch {
    // Storage refused (private mode, embedded webview) — follow the OS.
    return "system";
  }
}

type ThemeState = { pref: AppearancePref; system: Scheme };

let state: ThemeState = { pref: readAppearancePref(), system: systemScheme() };
const listeners = new Set<() => void>();

const emitChange = (): void => {
  for (const listener of listeners) listener();
};

export const scheme = (): Scheme => resolveScheme(state.pref, state.system);

let shellOverride: Scheme | null = null;

/** The browser chrome tints to the desk with the document (ticket
 *  #573): the head's theme-color meta carries the resolved scheme's
 *  paper value. The hexes are index.css's --tt-paper values —
 *  webThemePins holds this map and both HTML entries in byte-lockstep
 *  with the CSS, so the copies cannot drift silently. */
const PAPER_HEX: Record<Scheme, string> = {
  light: "#EDF2F8",
  dark: "#0B1119",
};

const render = (): void => {
  const resolved = shellOverride ?? scheme();
  document.documentElement.setAttribute("data-theme", resolved);
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", PAPER_HEX[resolved]);
};

/** The island's shell-driven scheme (#372): the native side owns the
 *  resolved scheme and pushes it across the bridge — the OS listener
 *  and any stored pref stand down while an override is active. The SPA
 *  never calls this; its users own their own pref. */
export function applyShellScheme(scheme: Scheme): void {
  shellOverride = scheme;
  render();
}

export const getThemeState = (): ThemeState => state;

export const subscribeTheme = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Persist the override (the Settings control lands with its ticket)
 *  and re-render immediately. */
export function setAppearancePref(pref: AppearancePref): void {
  try {
    localStorage.setItem(APPEARANCE_KEY, pref);
  } catch {
    // A refused write degrades to a session-only preference.
  }
  state = { ...state, pref };
  render();
  emitChange();
}

const onSystemChange = (e: MediaQueryListEvent): void => {
  state = { ...state, system: e.matches ? "dark" : "light" };
  render();
  emitChange();
};

let installed = false;

/** Wire the store to the document and the OS listener. Idempotent —
 *  StrictMode and the island entry both call it. */
export function installTheme(): void {
  if (installed) return;
  installed = true;
  render();
  darkQuery().addEventListener("change", onSystemChange);
}

/** React seam: the reactive read the future Settings control consumes.
 *  Nothing in this ticket renders from it yet — components style from
 *  the token utilities, which follow data-theme in CSS. */
export function useAppearance(): {
  pref: AppearancePref;
  scheme: Scheme;
  setPref: (pref: AppearancePref) => void;
} {
  const snapshot = useSyncExternalStore(subscribeTheme, getThemeState);
  return {
    pref: snapshot.pref,
    scheme: resolveScheme(snapshot.pref, snapshot.system),
    setPref: setAppearancePref,
  };
}
